import { formatDateRangeShortYear } from '@/lib/date-format';
import type { CalendarEvent } from '@/lib/types';

/**
 * Hand-curated overrides for university city slugs that lose information when
 * ASCII-slugged (diacritics, multi-word capitalization that title-case can't
 * reconstruct correctly). Anything not listed here falls through to title-case
 * with hyphen-as-space.
 */
const CITY_DISPLAY_OVERRIDES: Record<string, string> = {
  'los-banos': 'Los Baños',
  'quezon-city': 'Quezon City',
  'kuala-lumpur': 'Kuala Lumpur',
  'johor-bahru': 'Johor Bahru',
  'shah-alam': 'Shah Alam',
  'tanjong-malim': 'Tanjong Malim',
  'kota-samarahan': 'Kota Samarahan',
  'kota-kinabalu': 'Kota Kinabalu',
  'kuala-nerus': 'Kuala Nerus',
  'kuala-terengganu': 'Kuala Terengganu',
  'bestari-jaya': 'Bestari Jaya',
  'parit-raja': 'Parit Raja',
  'cagayan-de-oro': 'Cagayan de Oro',
  'al-ahsa': 'Al-Ahsa',
};

/**
 * Render a university's city slug as a display name.
 *  1. Hardcoded override (handles diacritics like Baños that ASCII slugs lose)
 *  2. Title-case fallback with hyphen→space
 *
 * Replaces the previous naive `city.charAt(0).toUpperCase() + city.slice(1)`
 * which produced "Los-banos" / "Quezon-city" on Philippine campus pages.
 *
 * Refs: almanak-uj7qp.13
 */
export function formatUniversityCityName(citySlug: string): string {
  if (!citySlug) return '';
  const lower = citySlug.toLowerCase();
  const override = CITY_DISPLAY_OVERRIDES[lower];
  if (override) return override;

  // Generic title-case fallback. Treats hyphens as word boundaries.
  return lower
    .split('-')
    .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
}

export function getAcademicYearRange(events: CalendarEvent[]): string {
  if (events.length === 0) return '';
  const years = events.map(e => parseInt(e.dateStart.slice(0, 4), 10));
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);
  return minYear === maxYear ? `${minYear}/${minYear + 1}` : `${minYear}/${maxYear}`;
}

export const INTRO_TEMPLATES: Record<string, (name: string, count: number, city: string, yr: string) => string> = {
  en: (name, count, city, yr) =>
    `${name} in ${city} has ${count} key academic dates for the ${yr} academic year. Browse the full schedule below.`,
  id: (name, count, city, yr) =>
    `${name} di ${city} memiliki ${count} jadwal akademik penting untuk tahun akademik ${yr}. Lihat jadwal lengkap di bawah.`,
  ms: (name, count, city, yr) =>
    `${name} di ${city} mempunyai ${count} tarikh akademik penting untuk tahun akademik ${yr}. Layari jadual penuh di bawah.`,
  tl: (name, count, city, yr) =>
    `Ang ${name} sa ${city} ay may ${count} mahahalagang petsa para sa taong akademiko ${yr}. Tingnan ang buong iskedyul sa ibaba.`,
};

export function findEventByType(events: CalendarEvent[], type: string): CalendarEvent | null {
  return events.find(e => e.type === type) || null;
}

export function findEventByKeyword(events: CalendarEvent[], keywords: string[]): CalendarEvent | null {
  return events.find(e =>
    keywords.some(kw => e.title.toLowerCase().includes(kw) || (e.titleLocal?.toLowerCase().includes(kw)))
  ) || null;
}

export function formatDateRange(event: CalendarEvent, lang: string): string {
  return formatDateRangeShortYear(event.dateStart, event.dateEnd, lang);
}
